import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { useAppSetting } from "@swa-ui/application";
import { useDeviceInfo } from "@swa-ui/browser";
import { Grid, Icon, Link } from "@swa-ui/core";
import { Form, SubmitFormField, TextFormField, useForm, yupResolver } from "@swa-ui/form";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import { Countdown } from "../../packages/@swa-ui/core/src/Countdown";
import { MfaHeading } from "../MfaHeading";
import { MfaHelpLink } from "../MfaHelpLink";
import styles from "./MfaEnterPasscodeForm.module.scss";
import { fieldMaxLengths, getMfaEnterPasscodeFormSchema } from "./MfaEnterPasscodeForm.schema";

/**
 * MfaEnterPasscodeForm component is used to collect the passcode from the user.
 */
export const MfaEnterPasscodeForm = (props) => {
  const {
    contact,
    loading,
    onResend,
    onRestart,
    onSubmit,
    passwordResendSuccess,
    validationErrorKey,
  } = props;
  const { screenSize } = useDeviceInfo() ?? {};
  const mfaResendDelayInSeconds = useAppSetting("mfaResendDelayInSeconds", 60);
  const mfaResendAriaLiveDelay = useAppSetting("mfaResendAriaLiveDelay", 15);

  const formMethods = useForm({ resolver: yupResolver(getMfaEnterPasscodeFormSchema()) });
  const [retryCountdownRestartKey, setRetryCountdownRestartKey] = useState(0);
  const [retryCountDownDone, setRetryCountDownDone] = useState(false);

  useEffect(() => {
    if (passwordResendSuccess) {
      setRetryCountdownRestartKey((prev) => prev + 1);
      setRetryCountDownDone(false);
      formMethods.clearErrors("passcode");
      formMethods.setFocus("passcode");
      formMethods.setValue("passcode", "");
    }
  }, [passwordResendSuccess]);

  useEffect(() => {
    if (validationErrorKey) {
      formMethods.setError("passcode", {
        message: i18n(validationErrorKey),
      });
      formMethods.setValue("passcode", "");
      formMethods.setFocus("passcode");
    }
  }, [validationErrorKey]);

  return (
    <Grid {...getGridProps()}>
      <MfaHeading
        heading={i18n("MfaEnterPasscodeForm__HEADING")}
        key="enter-passcode-heading"
        subheading={
          <div className={styles.subheading}>
            <span>{i18n(`MfaEnterPasscodeForm__PROMPT_${contact.deviceType}`)}</span>{" "}
            <span className={styles.contactValue}>{contact.value}.</span>{" "}
            <span>{i18n("MfaEnterPasscodeForm__EXPIRATION_NOTICE")}</span>
          </div>
        }
      />
      <Form methods={formMethods} onSubmit={onSubmit} noValidate>
        <TextFormField {...getPasscodeProps()} />
        {passwordResendSuccess && !retryCountDownDone && (
          <div className={classNames(styles.fullWidth, styles.resend)}>
            <Icon {...getRequestPasscodeIconProps()} />
            {i18n(`MfaEnterPasscodeForm__RESEND_SUCCESS_${contact.deviceType}`)}
          </div>
        )}
        <div className={styles.fullWidth}>
          {i18n("MfaEnterPasscodeForm__RESEND_PROMPT")} <Countdown {...getCountdownProps()} />
        </div>
        <Link
          className={classNames(styles.fullWidth, styles.actionLinks)}
          disabled={loading || !retryCountDownDone}
          onClick={onResend}
        >
          {i18n("MfaEnterPasscodeForm__RESEND_PASSCODE")}
        </Link>
        <Link
          className={classNames(styles.fullWidth, styles.actionLinks)}
          disabled={loading || !retryCountDownDone}
          onClick={onRestart}
        >
          {i18n("MfaEnterPasscodeForm__RESTART")}
        </Link>
        <MfaHelpLink className={classNames(styles.fullWidth, styles.needHelp)} />
        <SubmitFormField {...getSubmitProps()}>
          {i18n("MfaEnterPasscodeForm__SUBMIT")}
        </SubmitFormField>
      </Form>
    </Grid>
  );

  function getGridProps() {
    return {
      gutterSpacing: 16,
      numberOfColumns: screenSize === "small" ? 4 : 12,
      rowSpacing: 16,
    };
  }

  function getCountdownProps() {
    return {
      ariaLabelCountingDown: i18n.bind(this, "MfaEnterPasscodeForm__RESEND_TIMER_ARIA"),
      ariaLabelDone: i18n("MfaEnterPasscodeForm__RESEND_LINK_ENABLED_ARIA_LIVE"),
      ariaLiveDone: i18n("MfaEnterPasscodeForm__RESEND_LINK_ENABLED_ARIA_LIVE"),
      ariaLiveRestarted: i18n(`MfaEnterPasscodeForm__RESEND_SUCCESS_${contact.deviceType}`),
      ariaLiveWarning: i18n.bind(this, "MfaEnterPasscodeForm__RESEND_TIMER_ARIA"),
      durationInSeconds: mfaResendDelayInSeconds,
      onDone: () => setRetryCountDownDone(true),
      paused: loading,
      restartKey: retryCountdownRestartKey,
      warningIntervalInSeconds: mfaResendAriaLiveDelay,
    };
  }

  function getPasscodeProps() {
    return {
      className: styles.fullWidth,
      componentProps: {
        maxLength: fieldMaxLengths.passcode,
        readOnly: loading,
      },
      label: i18n("MfaEnterPasscodeForm__PASSCODE"),
      name: "passcode",
    };
  }

  function getSubmitProps() {
    return {
      className: styles.fullWidth,
      componentProps: {
        fullWidth: true,
        submitInProgress: loading,
      },
      name: "submit",
    };
  }

  function getRequestPasscodeIconProps() {
    return {
      "aria-label": i18n("MfaEnterPasscodeForm__RESEND_SUCCESS_ICON_ARIA_LABEL"),
      className: styles.icon,
      name: "Info2",
      size: "size20",
    };
  }
};

MfaEnterPasscodeForm.propTypes = {
  /**
   * The contact information for the user.
   */
  contact: PropTypes.shape({
    deviceType: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,

  /**
   * Indicates if the form is in a loading state.
   */
  loading: PropTypes.bool,

  /**
   * Callback function to handle resending the passcode.
   */
  onResend: PropTypes.func.isRequired,

  /**
   * Callback function to handle restarting the MFA process.
   */
  onRestart: PropTypes.func.isRequired,

  /**
   * Callback function to handle form submission.
   */
  onSubmit: PropTypes.func.isRequired,

  /**
   * Indicates if the passcode resend was successful.
   */
  passwordResendSuccess: PropTypes.bool,

  /**
   * The validation i18n error key to display.
   */
  validationErrorKey: PropTypes.string,
};
